// Backend (@ulp/api) tüketici uçlarının (/api/app/*) yanıt tipleri — PWA ve mobil istemci ortak kaynağı.

export interface OtpStartResponse {
  challengeId: string;
  expiresAt: string;
  /** Kodun gittiği kanal (v1: email). */
  channel?: 'email' | 'sms';
  /** Maskeli hedef ("a****@g****.com"). */
  destination?: string;
  devCode?: string; // OTP_DEV_ECHO=true iken dev kolaylığı
}

export interface OtpVerifyResponse {
  token: string;
  memberId: string;
  tenantId: string;
  /** Bu girişte yeni üyelik açıldı mı. */
  isNewMember?: boolean;
  /** Misafir dönüşümünde taşınan sipariş sayısı (Faz 6). */
  convertedGuestOrders?: number;
}

/** GET /app/bootstrap — giriş öncesi (anonim). */
export interface BootstrapView {
  tenant: { name: string; slug: string };
  guestCheckout: boolean;
  locales: string[];
  defaultLocale: string;
  /** Giriş kanalı (v1: email; SMS sağlayıcısı sonra). */
  authChannel?: 'email' | 'sms';
  brand?: { logoUrl: string | null; primaryColor: string | null; darkLogoUrl?: string | null; accentColor?: string | null };
  termsVersion?: string;
  /** Phase 1: işletmede açık ürün modülleri (capability anahtarları). */
  capabilities?: string[];
  /** Uygulama kanalları (N1): web her zaman; Super APP / markalı uygulama modül + yayındaki uygulamaya göre. */
  channels?: ChannelsView;
  /** Yalnız native istemci (x-app-id): bu uygulama bu işletmeye hizmet verebilir mi + zorunlu güncelleme sürümü. */
  app?: ClientAppView;
}

export interface ChannelsView {
  web: true;
  superApp: { available: boolean; appStoreUrl: string | null; playUrl: string | null };
  whiteLabel: { name: string; appStoreUrl: string | null; playUrl: string | null } | null;
}

export interface ClientAppView {
  kind: 'SUPER_APP' | 'WHITE_LABEL' | 'UNKNOWN';
  name: string | null;
  allowed: boolean;
  reason: 'NATIVE_APP_DISABLED' | 'WRONG_TENANT_APP' | 'APP_SUSPENDED' | string | null;
  minVersion: string | null;
  latestVersion: string | null;
  appStoreUrl: string | null;
  playUrl: string | null;
}

// ---- Phase 1: uygulamasız üyelik + Wallet kartı ----

export interface EnrollRequest {
  firstName: string;
  lastName?: string;
  phone: string;
  acceptTerms: boolean;
  termsVersion?: string;
  smsMarketing?: boolean;
  emailMarketing?: boolean;
  pushMarketing?: boolean;
  branchCode?: string;
  otpChallengeId?: string;
  otpCode?: string;
  /** Bot tuzağı — boş kalmalı. */
  website?: string;
}

export type EnrollResponse =
  | { token: string; memberId: string; isNewMember: true; member: { firstName: string; membershipNumber: string | null; points: number }; phoneVerified: boolean; walletPass: 'PENDING' }
  | { token: string; memberId: string; isNewMember: false; phoneVerified: true }
  | { status: 'VERIFY_PHONE'; challengeId: string; destination: string; devCode?: string };

export interface CardView {
  /** Statik kart QR'ı (yalnız puan kazanımı). */
  qr: string;
  displayPrefix: string;
  membershipNumber: string | null;
  firstName: string | null;
}

export type WalletProviderKey = 'APPLE' | 'GOOGLE';

export interface WalletPassStatusView {
  available: Record<WalletProviderKey, boolean>;
  cards: Array<{ provider: WalletProviderKey; status: 'PENDING' | 'ISSUED' | 'FAILED' | 'REVOKED'; installed: boolean; lastSyncedAt: string | null }>;
}

export type WalletPassRequestResult = { provider: WalletProviderKey; status: 'ISSUED'; addUrl: string } | { provider: WalletProviderKey; status: 'PENDING' };

export interface AccountOptionsView {
  smsAvailable: boolean;
  recovery: Array<'SMS_OTP' | 'IN_STORE'>;
}

export interface OtpStepView {
  challengeId: string;
  destination: string;
  devCode?: string;
}

export interface SessionGrant {
  token: string;
  memberId: string;
  isNewMember: boolean;
}

/** POST /app/guest/session — misafir oturumu. */
export interface GuestSessionResponse extends OtpVerifyResponse {
  guest: true;
}

export interface WalletView {
  currency: string;
  balance: string;
  held: string;
  spendable: string;
}

export interface TierRef {
  name: string;
  level: number;
}

export interface TierProgress {
  earned: number;
  target: number;
  percent: number;
  remaining: number;
}

export interface StampCardView {
  programId: string;
  programName: string | null;
  stamps: number;
  target: number | null;
  completedCount: number;
}

export interface HomeView {
  member: { id: string; fullName: string | null };
  wallet: WalletView;
  points: { balance: string; reserved: string; spendable: string };
  tier: TierRef | null;
  nextTier: TierRef | null;
  tierProgress: TierProgress | null;
  stampCards: StampCardView[];
  offers: OfferView[];
}

export interface OfferView {
  id: string;
  name: string;
  title: string;
  description: string | null;
  endsAt: string | null;
}

export interface ScanTokenResponse {
  token: string;
  nonce: string;
  expiresAt: string;
  tipPercent: number;
}

export interface OptionChoice {
  id: string;
  externalId: string | null;
  name: string;
  priceDelta: string; // işaretli TL farkı (legacy)
  /** Faz 5: kuruş. */
  priceDeltaKurus?: number;
  isDefault: boolean;
  /** Aynı seçenekten en fazla adet (1 = adet yok). */
  quantityMax?: number;
}

export interface OptionGroup {
  id: string;
  externalId: string | null;
  name: string;
  selectionType: 'single' | 'multiple';
  minSelection: number;
  maxSelection: number | null;
  tier: number; // 1=Ebat 2=Ekstra 3=Genel
  isGlobal: boolean;
  /** Faz 5 nested: yalnız bu seçenek seçiliyken açılır. */
  parentChoiceId?: string | null;
  sortOrder?: number;
  choices: OptionChoice[];
}

export type AvailabilityState = 'AVAILABLE' | 'SOLD_OUT' | 'TEMPORARILY_UNAVAILABLE' | 'SCHEDULED' | 'BRANCH_DISABLED' | 'CHANNEL_DISABLED';

/** Menü kalemi (GET /app/menu, /app/menu/view.items). Eski alanlar aynen; Faz 5 alanları isteğe bağlı. */
export interface MenuItem {
  id: string;
  externalId: string | null;
  name: string;
  price: string; // decimal string (TRY)
  categoryId: string | null;
  categoryName: string | null;
  optionGroups: OptionGroup[];
  // ---- Faz 5 ----
  priceKurus?: number;
  basePriceKurus?: number;
  priceSource?: string;
  displayName?: string;
  description?: string | null;
  imageUrl?: string | null;
  sortOrder?: number;
  visibility?: 'VISIBLE' | 'HIDDEN' | 'FEATURED';
  featured?: boolean;
  allergens?: string[];
  saleMeasure?: string;
  availability?: AvailabilityState;
  orderable?: boolean;
  availableUntil?: string | null;
}

export interface MenuView {
  version: number;
  locale: string;
  branchId: string | null;
  channel: string;
  generatedAt: string;
  categories: Array<{ id: string; name: string; sortOrder: number; itemIds: string[] }>;
  items: MenuItem[];
}

export type FulfillmentStatus = 'PLACED' | 'PREPARING' | 'READY' | 'COMPLETED' | 'CANCELLED';

/** Canonical sipariş durumu (Faz 2). */
export type OrderState =
  | 'DRAFT' | 'AWAITING_PAYMENT' | 'PAYMENT_UNKNOWN' | 'PAID' | 'PAYMENT_FAILED'
  | 'SUBMITTING' | 'POS_SYNC_PENDING' | 'POS_SYNC_FAILED' | 'SUBMITTED'
  | 'ACCEPTED' | 'PREPARING' | 'READY' | 'HANDED_OVER' | 'COMPLETED'
  | 'CANCEL_REQUESTED' | 'CANCELLED' | 'REJECTED' | 'REFUND_PENDING' | 'PARTIALLY_REFUNDED' | 'REFUNDED' | 'EXPIRED' | 'DELIVERING' | 'DELIVERED';

export interface OrderLineView {
  id: string;
  productId: string | null;
  name: string;
  quantity: number;
  unitPriceKurus: number;
  modifiersKurus: number;
  lineTotalKurus: number;
  modifiers: string[];
  note: string | null;
}

export interface OrderView {
  id: string;
  externalId: string;
  memberId: string | null;
  status: string;
  channel: string;
  fulfillmentType: string | null;
  fulfillmentStatus: FulfillmentStatus | null;
  pickupCode: string | null;
  paymentMethod: 'WALLET' | 'CARD' | 'CASH' | null;
  total: string;
  currency: string;
  readyAt: string | null;
  // ---- Faz 2 (canonical) ----
  state?: OrderState;
  branchId?: string | null;
  pickupNumber?: string | null;
  /** Gösterilecek numara: pickupNumber varsa o, yoksa pickupCode. */
  displayNumber?: string | null;
  totalKurus?: number;
  subtotalKurus?: number;
  isPaid?: boolean;
  paidAt?: string | null;
  acceptedAt?: string | null;
  completedAt?: string | null;
  cancelledAt?: string | null;
  cancelReason?: string | null;
  note?: string | null;
  scheduledFor?: string | null;
  placedAt?: string;
  /** Faz 6: satır snapshot'ları (detay ekranı). */
  lines?: OrderLineView[];
  replayed?: boolean;
}

/** GET /app/orders/:id/events — zaman çizelgesi. */
export interface OrderEventView {
  id: string;
  type: string;
  fromState: OrderState | null;
  toState: OrderState | null;
  actorType: string;
  occurredAt: string;
  payload?: Record<string, unknown> | null;
}

// ---- Hesabım ----

export interface ProfileView {
  member: {
    id: string;
    fullName: string | null;
    phone: string | null;
    /** false: uygulamada beyan edildi, SMS ile doğrulanmadı (v1). */
    phoneVerified?: boolean;
    email: string | null;
    emailVerified?: boolean;
    membershipNumber?: string | null;
    marketingConsent?: boolean;
    enrolledAt: string;
  };
  tier: TierRef | null;
  points: { spendable: string; lifetimeEarned: number };
  wallet: { currency: string; spendable: string };
  stats: { orderCount: number; stamps: number; stampCardsCompleted: number };
}

export interface OrderHistoryItem {
  id: string;
  externalId: string;
  placedAt: string;
  status: string; // PENDING | PAID | PARTIALLY_REFUNDED | REFUNDED | CANCELLED
  channel: string; // POS | MOBILE | WEB | KIOSK
  fulfillmentStatus: FulfillmentStatus | null;
  pickupCode: string | null;
  total: string;
  currency: string;
  pointsEarned: number;
  items: Array<{ name: string; quantity: number; lineTotal: string; options: string[] }>;
}

export interface PointHistoryItem {
  id: string;
  type: 'EARN' | 'REDEEM' | 'RESERVE' | 'RESERVE_CANCEL' | 'RESERVE_COMMIT' | 'REVERSE' | 'ADJUST' | 'EXPIRE' | string;
  amount: number;
  balanceAfter: number;
  reason: string | null;
  refType: string | null;
  refId: string | null;
  createdAt: string;
}

// ---- İşletme profili (GET /app/config) ----

export type VenueType = 'SELF_SERVICE_CAFE' | 'FAST_CASUAL' | 'A_LA_CARTE';

export interface VenueFeatures {
  mobileOrder: boolean;
  payAtCounter: boolean;
  payWithWallet: boolean;
  scanToPay: boolean;
  stampCard: boolean;
  tiers: boolean;
  offers: boolean;
  readyNotification: boolean;
  tips: boolean;
  visitBasedEarn: boolean;
  courseGroupedMenu: boolean;
}

/** Commerce özellikleri (Faz 1+); profil ∧ capability ∧ flag sonrası etkin değer. */
export type CommerceFeatureKey =
  | 'pickupNumbers' | 'tables' | 'tableQr' | 'groupOrdering' | 'splitPayment' | 'guestCheckout'
  | 'scheduledOrders' | 'kds' | 'autoAcceptOrders' | 'subscriptions' | 'weightBasedProducts' | 'customerDisplay';

export interface VenueLabels {
  orderTab: string;
  pickupCode: string;
  readyTitle: string;
  handoverStep: string;
  payAtCounter: string;
  branchPicker?: string;
}

export interface VenueProfile {
  venueType: VenueType;
  features: VenueFeatures;
  labels: VenueLabels;
}

export type ServiceType = 'DINE_IN' | 'TAKEAWAY' | 'DELIVERY' | 'PICKUP';

/** Şube kartı (GET /app/config.branches, GET /app/branches). */
export interface BranchView {
  id: string;
  code: string;
  name: string;
  address: string | null;
  lat: number | null;
  lng: number | null;
  timezone: string;
  prepTimeMinutes: number;
  minOrderKurus: number;
  isDefault: boolean;
  acceptingOrders: boolean;
  notAcceptingReason: 'INACTIVE' | 'CLOSED' | 'PAUSED' | 'OUTSIDE_HOURS' | null;
  pausedUntil: string | null;
  serviceTypes: Partial<Record<ServiceType, boolean>>;
}

export interface DegradedFeature {
  feature: string;
  reason: string;
  detail?: string | null;
}

/** Commerce Faz 1: profil (etkin özellikler) + capability/seviye + şubeler. Eski alanlar korunur. */
export interface AppConfig {
  tenant: { name: string; slug: string; timezone?: string };
  profile: VenueProfile & {
    profileType?: string;
    serviceTypes?: Record<ServiceType, boolean>;
    features: VenueFeatures & Partial<Record<CommerceFeatureKey, boolean>>;
  };
  commerce?: {
    level: string;
    connector: string;
    connectionStatus: string;
    capabilities: Record<string, boolean>;
    degraded: DegradedFeature[];
    flags: Record<string, boolean>;
  };
  branches?: BranchView[];
  defaultBranchId?: string | null;
}

/** Config gelene kadar kullanılan varsayılan (self servis kafe ön ayarıyla aynı). */
export const DEFAULT_PROFILE: VenueProfile = {
  venueType: 'SELF_SERVICE_CAFE',
  features: {
    mobileOrder: true,
    payAtCounter: true,
    payWithWallet: true,
    scanToPay: true,
    stampCard: true,
    tiers: true,
    offers: true,
    readyNotification: true,
    tips: false,
    visitBasedEarn: false,
    courseGroupedMenu: false,
  },
  labels: {
    orderTab: 'Sipariş',
    pickupCode: 'Alım kodu',
    readyTitle: 'Siparişin hazır ☕',
    handoverStep: 'Teslim edildi',
    payAtCounter: 'Kasada öde',
    branchPicker: 'Şube',
  },
};

// ---- Super APP v1 · F3: ödül cüzdanı, katalog, seviye yolculuğu ----

export type IssuedRewardStatus = 'AVAILABLE' | 'RESERVED' | 'USED' | 'EXPIRED' | 'CANCELLED';

export interface IssuedRewardView {
  id: string;
  reward: { id: string; name?: string; type?: string; typeLabel?: string; imageUrl?: string | null; description?: string | null };
  status: IssuedRewardStatus;
  source: string;
  costPoints: number | null;
  issuedAt: string;
  expiresAt: string | null;
  usedAt: string | null;
}

export interface RewardWalletView {
  available: IssuedRewardView[];
  history: IssuedRewardView[];
}

export interface RewardCatalogItem {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  type: string;
  typeLabel: string;
  costPoints: number;
  validityDays: number | null;
  minBasketKurus: number | null;
}

export interface TierStep {
  id: string;
  name: string;
  level: number;
  metric: 'POINTS' | 'SPEND' | 'ORDERS' | 'VISITS' | string;
  min: number;
  windowDays: number | null;
  benefits: string[];
  description: string | null;
  color: string | null;
}

export interface TierJourneyView {
  currentTierId: string | null;
  tiers: TierStep[];
  history: Array<{ id: string; from: string | null; to: string | null; reason: string; at: string }>;
}

// ---- Super APP v1 · F4: masa rezervasyonu (ana kayıt platformda; MaydayPOS'a senkron) ----

export type ReservationStatus = 'PENDING' | 'CONFIRMED' | 'REJECTED' | 'CANCELLED' | 'SEATED' | 'NO_SHOW' | 'COMPLETED';

export interface ReservationBranchOption {
  id: string;
  name: string;
  address: string | null;
}

export interface ReservationSettingsView {
  enabled: boolean;
  branches: ReservationBranchOption[];
  minPartySize: number;
  maxPartySize: number;
  /** Kaç gün ileriye rezervasyon açık. */
  bookingWindowDays: number;
  /** İşletme onayı gerekiyor mu (v1 kararı D5: evet). */
  requiresApproval: boolean;
  noteMaxLength: number;
  /** İşletmenin rezervasyon notu/politikası (ör. "15 dk gecikmede masa bekletilmez"). */
  policyText: string | null;
}

export interface ReservationSlot {
  /** Yerel saat "19:30" */
  time: string;
  startsAt: string;
  available: boolean;
  /** Bu slotta kalan kişi kapasitesi (bilgi amaçlı). */
  remainingSeats: number | null;
}

export interface ReservationSlotsView {
  date: string;
  closed: boolean;
  closedReason: string | null;
  slots: ReservationSlot[];
}

export interface ReservationView {
  id: string;
  /** Kısa referans (kasada söylenir): "R7K2QX" */
  code: string;
  branch: ReservationBranchOption;
  startsAt: string;
  endsAt: string;
  partySize: number;
  status: ReservationStatus;
  note: string | null;
  contactName: string;
  contactPhone: string | null;
  tableLabel: string | null;
  decisionNote: string | null;
  createdAt: string;
  canCancel: boolean;
}

export interface ReservationListView {
  upcoming: ReservationView[];
  past: ReservationView[];
}

// ---- Super APP v1 · F7 (D9): "Fişimi ekle" — 3. parti POS satışını üyeye bağlama talebi ----

export type ReceiptClaimStatus = 'PENDING' | 'MATCHED' | 'REJECTED' | 'EXPIRED';

export interface ReceiptClaimView {
  id: string;
  branch: ReservationBranchOption | null;
  receiptNo: string;
  totalKurus: number;
  purchasedOn: string;
  status: ReceiptClaimStatus;
  pointsAwarded: number | null;
  rejectReason: string | null;
  createdAt: string;
}
